import identity from 'lodash/identity';
import { computed, makeObservable } from 'mobx';
import { JobModel, MomentSelector } from '..';
import { xor } from '../../core';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { Comment, Reaction } from '../comment';
import { Generic, Moment, MomentModel, SubTopic, Topic, Transcript } from '../moment';
import { Speaker, SpeakerModel } from '../speaker';
import { PlayerItemSource } from './playerItemSource';

export const RedactionMomentTypes = {
  FaceBlur: 'FaceBlur',
  AudioMuted: 'AudioMuted',
  ScreenBlur: 'ScreenBlur',
  LicenseBlur: 'LicenseBlur',
  BlankScreen: 'BlankScreen',
} as const;

export type RedactionMomentType = keyof typeof RedactionMomentTypes;

type Props = {
  momentId?: string,
  speakerId?: string,
  commentId?: string,
  reactionId?: string
} & BindingProps<{
  source?: PlayerItemSource
}>

export type PlayerItemTargetEntityType =
  'Moment' |
  'Speaker' |
  'Comment' |
  'Reaction' |
  'None';

export type PlayerItemTargetType =
  'Topic' |
  'SubTopic' |
  'Paragraph' |
  'Chapter' |
  'Moment' |
  'ActionItem' |
  'Transcript' |
  'Speaker' |
  'Company' |
  'Comment' |
  'Reaction' |
  'None';

  export type PlayerItemMomentType =
  'Answer' |
  'Question' |
  'None';

export type PlayerItemPlaybackRole =
  'None' |
  'Topic' |
  'ShadowTopic' |
  'SubTopic' |
  'Speaker' |
  'Highlight' |
  'HighlightTopic' |
  'HighlightSubTopic' |
  'HighlightMoment' |
  'Full';

export type PlayerItemTarget =
  Topic |
  SubTopic |
  Generic |
  Transcript |
  Speaker |
  Moment |
  Comment |
  Reaction;

/**
 * Represents an object like a Moment or a Speaker which has some sort of significance in the context of a Player.
 * Each entity should have a single Item assigned, with various props exposed for determining the behaviour.
 */
export class PlayerItem<T extends PlayerItemTarget = PlayerItemTarget>
  extends StoreNode {

  constructor(store: Store, props?: Partial<Props>) {
    super(store, props);
    makeObservable(this, {
      source: computed,
      selector: computed,
      jobId: computed,
      job: computed,
      moment: computed,
      speaker: computed,
      comment: computed,
      reaction: computed,
      targetEntityType: computed,
      targetType: computed,
      targetId: computed,
      target: computed,
      isTopicNode: computed,
      isQuestionNode: computed,
      isAnswerNode: computed,
      isValidTopicNode: computed,
      isSubTopicNode: computed,
      isValidSubTopicNode: computed,
      isTranscriptNode: computed,
      isSpeakerNode: computed,
      siblingSpeakerItem: computed,
      parentTopicItem: computed,
      childSubTopicItems: computed,
      isSelected: computed,
      isIndirectlySelected: computed,
      areAllChildSubTopicsSelected: computed,
      areNoChildSubTopicsSelected: computed,
      areSomeChildSubTopicsSelected: computed,
      useInDirector: computed,
      useInReporter: computed,
      showOnPreviewTimeline: computed,
      playbackRole: computed,
      playbarRenderOrder: computed,
      showOnPlaybar: computed,
      showOnMarkerBar: computed,
      markerBarRenderOrder: computed,
      showAsActiveTopic: computed,
      showAsActiveSubTopic: computed,
      showAsActiveSpeaker: computed,
      showAsActiveQuestion: computed,
      showAsActiveAnswer: computed,
      showInSpeakerIdForm: computed,
      showNextTopicButton: computed,
      showNextSubTopicButton: computed,
      showNextSpeakerButton: computed,
      showInTranscriptsBox: computed,
      showInDetailsPanel: computed,
      momentHasSpeaker: computed,
      momentHasPrivateSpeaker: computed,
      momentSpeakerId: computed,
      momentSpeaker: computed,
      isMomentSpeakerSelected: computed,
      showInIndexSection: computed,
      showInCommentsSection: computed,
      isCommentNode: computed,
      isReactionNode: computed,
      isComment: computed,
      isReply: computed,
      isReaction: computed,
      isRedactionMomentType: computed,
    });

    this.momentId = props?.momentId || null;
    this.speakerId = props?.speakerId || null;
    this.commentId = props?.commentId || null;
    this.reactionId = props?.reactionId || null;

    if (!this.jobId || !xor(this.momentId, this.speakerId, this.commentId, this.reactionId))
      console.error(`The input parameters for PlayerItem are invalid. Make sure you specify a 'jobId' and either a 'momentId', a 'speakerId' or a 'commentId', but not more than one of these.`);
  }

  readonly momentId: string | null = null;
  readonly speakerId: string | null = null;
  readonly commentId: string | null = null;
  readonly reactionId: string | null = null;

  get key() {
    return this.targetId;
  }

  get source(): PlayerItemSource | null {
    return this.getResolvedProp('source');
  }

  get selector(): MomentSelector | null {
    return this.source?.selector || null;
  }

  get jobId(): string | null {
    return this.source?.jobId || null;
  }

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId) || null;
  }


  get moment(): MomentModel | null {
    return this.job?.maybeGetMoment(this.momentId) || null;
  }

  get speaker(): SpeakerModel | null {
    return this.store.maybeGetSpeaker(this.speakerId) || null;
  }

  get comment(): Comment | null {
    return this.job?.getComment(this.commentId) ?? null;
  }

  get reaction(): Reaction | null {
    return this.job?.getReaction(this.reactionId) ?? null;
  }


  get targetEntityType(): PlayerItemTargetEntityType {

    if (this.momentId)
      return 'Moment';
    if (this.speakerId)
      return 'Speaker';
    if (this.commentId)
      return 'Comment';
    if (this.reactionId)
      return 'Reaction';

    return 'None';
  }

  get targetType(): PlayerItemTargetType {

    switch (this.targetEntityType) {
      case 'Moment':
        const { moment } = this;
        if (!moment)
          return 'Moment';
        return moment.clipType;

      case 'Speaker':
        return 'Speaker';
      case 'Comment':
        return 'Comment';
      case 'Reaction':
        return 'Reaction';
    }

    return 'None';
  }

  get targetId(): string | null {
    switch (this.targetEntityType) {
      case 'Moment': return this.momentId;
      case 'Speaker': return this.speakerId;
      case 'Comment': return this.commentId;
      case 'Reaction': return this.reactionId;
    }
    return null;
  }

  get target(): T | null {
    switch (this.targetEntityType) {
      case 'Moment': return this.moment as T;
      case 'Speaker': return this.speaker as T;
      case 'Comment': return this.comment as T;
      case 'Reaction': return this.reaction as T;
    }
    return null;
  }

  get isTopicNode() {
    return this.targetType === 'Topic';
  }

  get isQuestionNode() {
    return this.moment?.momentType === 'Question';
  }

  get isAnswerNode() {
    return this.moment?.momentType === 'Answer';
  }
  
  /** 
   * Returns true if `targetType` is set to `Topic` and there is a valid `Moment` entity 
   * associated with this instance which has a `clipType` of `Topic`.
   */
  get isValidTopicNode(): boolean {
    return this.isTopicNode && !!this.moment?.isTopic;
  }

  get isSubTopicNode() {
    return this.targetType === 'SubTopic';
  }
  /** 
   * Returns true if `targetType` is set to `SubTopic` and there is a valid `Moment` entity 
   * associated with this instance which has a `clipType` of `SubTopic`.
   */
  get isValidSubTopicNode(): boolean {
    return this.isSubTopicNode && !!this.moment?.isSubtopic;
  }

  get isTranscriptNode() {
    return this.targetType === 'Transcript';
  }
  get isSpeakerNode() {
    return this.targetType === 'Speaker';
  }

  get siblingSpeakerItem(): PlayerItem<Speaker> | null {
    return this.source?.getItem<Speaker>(this.moment?.actualSpeakerId) || null;
  }

  get parentTopicItem(): PlayerItem<Topic> | null {
    return this.source?.getItem<Topic>(this.moment?.parentTopicId) || null;
  }

  get childSubTopicItems(): PlayerItem<SubTopic>[] {
    return this.moment?.childSubTopics
      .map(sub => this.source?.getItem(sub)!)
      .filter(identity) || [];
  }

  // #region Selection queries
  // -------

  get isSelected() {
    const { selector, targetId } = this;
    if (!selector || !targetId)
      return false;

    switch (this.targetType) {
      case 'Topic':
        return selector.isTopicSelected(targetId);

      case 'SubTopic':
        return selector.isSubTopicSelected(targetId);

      case 'Speaker':
        return selector.isSpeakerSelected(targetId);
      
      case 'Moment':
        if (this.isQuestionNode) {
          return selector.isQuestionSelected(targetId);
        }

        if (this.isAnswerNode) {
          return selector.isAnswerSelected(targetId);
        }

        return false;
    }

    return false;
  }

  get isIndirectlySelected() {
    const { selector, targetId, moment } = this;
    if (!selector || !targetId)
      return false;

    switch (this.targetType) {
      case 'Topic':
      case 'SubTopic':
        return (moment?.actualSpeakerId && selector.isSpeakerSelected(moment?.actualSpeakerId));
    }

    return false;
  }

  get areAllChildSubTopicsSelected() {
    const { selector, targetId } = this;
    if (this.targetType !== 'Topic' || !selector || !targetId)
      return false;
    return selector.areAllSubTopicsSelectedForTopic(targetId);
  }

  get areNoChildSubTopicsSelected() {
    const { selector, targetId } = this;
    if (this.targetType !== 'Topic' || !selector || !targetId)
      return false;
    return selector.areNoSubTopicsSelectedForTopic(targetId);
  }

  get areSomeChildSubTopicsSelected() {
    const { selector, targetId } = this;
    if (this.targetType !== 'Topic' || !selector || !targetId)
      return false;
    return selector.areSomeSubTopicsSelectedForTopic(targetId);
  }

  // #endregion

  get useInDirector(): boolean {

    const { selector, targetId } = this;
    if (!selector || !targetId)
      return false;

    switch (selector.mode) {

      case 'Topics':

        switch (this.targetType) {
          case 'Topic':
            return (
              this.isSelected &&
              !this.areSomeChildSubTopicsSelected);

          case 'SubTopic':
            const parentTopicId = this.moment?.parentTopic?.id;
            if (!parentTopicId)
              return false;

            return (
              this.isSelected &&
              !!this.parentTopicItem?.isSelected &&
              selector.areSomeSubTopicsSelectedForTopic(parentTopicId));
        }
        break;

      case 'Speakers':

        const spkId = this.moment?.actualSpeakerId;
        const isSpeakerSelected = !!spkId && selector.selectedSpeakerIds.has(spkId);

        return isSpeakerSelected;
    }

    return false;
  }

  get useInReporter() {
    return true;
  }

  get showOnPreviewTimeline() {
    // TODO: implement based on the item list rather than the raw selector/targetId check
    const { selector, targetId } = this;
    if (!selector || !targetId)
      return false;

    return this.useInDirector;
  }

  get playbackRole(): PlayerItemPlaybackRole {

    const { selector, targetId } = this;

    if (!selector || !targetId)
      return 'None';

    if (
      selector.highlightedMoment &&
      selector.highlightedMoment.id === this.targetId) {

      switch (this.targetType) {
        case 'Topic': return 'HighlightTopic';
        case 'SubTopic': return 'HighlightSubTopic';
        case 'Moment': return 'HighlightMoment';
      }

      return 'Highlight';
    }

    const isTopic = this.isTopicNode;
    const isSubTopic = this.isSubTopicNode;
    const isTranscriptNode = this.isTranscriptNode;
    const isAnswerNode = this.isAnswerNode;
    const isQuestionNode = this.isQuestionNode;

    if (isAnswerNode) {
      return 'SubTopic';
    } 

    if (isQuestionNode) {
      return 'Topic';
    }

    const speaker = this.moment?.actualSpeaker ?? null;
    const forSpeakersMode = (isTopic || isSubTopic || isTranscriptNode) && !!speaker;
    const isSpeakerSelected = selector.isSpeakerSelected(speaker);
    switch (selector.qualifiedMode) {
      case 'Topics':

        switch (this.targetType) {
          case 'Topic':
            if (!this.isSelected)
              return 'None';
            if (this.areSomeChildSubTopicsSelected)
              return 'ShadowTopic';
            return 'Topic';

          case 'SubTopic':
            if (!this.isSelected || !this.parentTopicItem?.isSelected)
              return 'None';
            return 'SubTopic';
        }
        break;

      case 'TopicsVideo':
        switch (this.targetType) {
          case 'Topic':
            return 'Topic';
          case 'SubTopic':
            return 'SubTopic';
        }
        break;

      case 'Speakers':
        return (forSpeakersMode && isSpeakerSelected) ? 'Speaker' : 'None';
      case 'SpeakersVideo':
        return forSpeakersMode ? 'Speaker' : 'None';
    }

    return 'None';
  }

  get playbarRenderOrder(): number {

    switch (this.playbackRole) {
      case 'Topic':
      case 'ShadowTopic': return 1;
      case 'Speaker': return 2;
      case 'SubTopic': return 3;

      case 'HighlightTopic': return 4;
      case 'HighlightSubTopic': return 5;
      case 'HighlightMoment': return 6;
      case 'Highlight': return 7;

      case 'Full': return 8;
    }

    return 0;
  }

  get showOnPlaybar() {
    return this.playbackRole !== 'None';
  }

  get showOnMarkerBar() {
    if (this.job?.isLiveStreaming)
      return false;

    return (
      this.isComment ||
      this.isReaction ||
      this.isRedactionMomentType);
  }

  get markerBarRenderOrder(): number {
    // TODO: implement markerBarRenderOrder based on clip type priority
    return 0;
  }

  get showAsActiveTopic(): boolean {
    return (
      this.isValidTopicNode &&
      (this.isSelected || !!this.selector?.isVideoOutputMode));
  }

  get showAsActiveSubTopic(): boolean {
    return (
      this.isValidSubTopicNode &&
      (this.isSelected || !!this.selector?.isVideoOutputMode));
  }

  get showAsActiveSpeaker(): boolean {
    return (
      this.momentHasSpeaker &&
      (this.isMomentSpeakerSelected || !!this.selector?.isVideoOutputMode));
  }

  get showAsActiveQuestion(): boolean {
    return (
      this.isQuestionNode &&
      (this.isSelected || !!this.selector?.isVideoOutputMode));
  }

  get showAsActiveAnswer(): boolean {
    return (
      this.isAnswerNode &&
      (this.isSelected || !!this.selector?.isVideoOutputMode));
  }

  get showInSpeakerIdForm(): boolean {
    return this.showAsActiveSpeaker && this.momentHasPrivateSpeaker;
  }

  get showNextTopicButton(): boolean {
    return !!this.moment && this.playbackRole === 'Topic';
  }
  get showNextSubTopicButton(): boolean {
    return !!this.moment && this.playbackRole === 'SubTopic';
  }
  get showNextSpeakerButton(): boolean {
    return (this.momentHasSpeaker && this.playbackRole === 'Speaker')
  }


  get showInTranscriptsBox(): boolean {
    return this.targetType === 'Transcript' && !!this.moment;
  }

  get showInDetailsPanel() {
    return (
      this.targetType === 'Moment' ||
      this.targetType === 'SubTopic') && !!this.moment;
  }

  get momentHasSpeaker(): boolean {
    return !!this.moment?.hasSpeaker;
  }

  get momentHasPrivateSpeaker(): boolean {
    const { moment } = this;
    if (!moment)
      return false;

    const { hasSpeaker, isSpeakerPrivate } = moment;
    return hasSpeaker && isSpeakerPrivate;
  }

  get momentSpeakerId(): string | null {
    return this.moment?.actualSpeakerId || null;
  }
  get momentSpeaker(): SpeakerModel | null {
    return this.moment?.actualSpeaker || null;
  }

  get isMomentSpeakerSelected(): boolean {
    return this.selector?.isSpeakerSelected(this.momentSpeakerId) || false;
  }

  // #region Index section

  get showInIndexSection(): boolean {
    return this.playbackRole !== 'None';
  }
  // #endregion

  // region Comments section 
  get showInCommentsSection(): boolean {
    return this.isCommentNode && !!this.comment;
  }

  get isCommentNode() {
    return this.targetType === 'Comment';
  }

  get isReactionNode() {
    return this.targetType === 'Reaction';
  }


  get isComment() {
    return this.isCommentNode && !this.comment?.isReply;
  }

  get isReply() {
    return this.isCommentNode && this.comment?.isReply;
  }

  get isReaction() {
    return this.isReactionNode;
  }

  get isRedactionMomentType() {
    return (
      this.targetType === 'Moment' &&
      this.moment &&
      Object.values(RedactionMomentTypes).includes(
        this.moment.momentType as RedactionMomentType
      )
    );
  }
  // #endregion
}